/**
 * Hook React pour gérer le flux de messages de scénario
 * Fournit un état réactif et des callbacks pour les composants UI
 */

import { useCallback, useReducer, useRef, useMemo } from 'react';
import { useSubscription } from '@apollo/client';
import { subscribeToMessages } from 'graph/queries';
import {
	ScenarioFlowState,
	ScenarioMessageCallbacks,
	ScenarioMessage,
	ProgressStep,
	ObjectType,
	ElementModification,
	NewElement,
	ScenarioResult
} from './types';
import {
	createInitialState,
	parseScenarioMessage,
	scenarioMessageReducer
} from './messageHandler';

// Action pour le reducer
type Action =
	| { type: 'MESSAGE'; payload: ScenarioMessage }
	| { type: 'RESET' }
	| { type: 'START'; payload: { query: string } };

// Reducer avec support des callbacks via ref
function createReducer(
	callbacksRef: React.MutableRefObject<ScenarioMessageCallbacks>
) {
	return function reducer(
		state: ScenarioFlowState,
		action: Action
	): ScenarioFlowState {
		switch (action.type) {
			case 'MESSAGE':
				return scenarioMessageReducer(
					state,
					action.payload,
					callbacksRef.current
				);
			case 'RESET':
				return createInitialState();
			case 'START':
				return {
					...createInitialState(),
					isRunning: true,
					query: action.payload.query
				};
			default:
				return state;
		}
	};
}

export interface UseScenarioMessagesOptions {
	projectId: string | null;
	enabled?: boolean;
	callbacks?: ScenarioMessageCallbacks;
}

export interface UseScenarioMessagesReturn {
	// État
	state: ScenarioFlowState;
	isRunning: boolean;
	progress: number;
	currentStep: ProgressStep | null;
	error: string | null;

	// Résultats partiels
	news: string | null;
	scenarioInfo: {
		name: string | null;
		description: string | null;
		analysis: string | null;
	};
	results: ScenarioFlowState['results'];
	finalResult: ScenarioResult | null;

	// Actions
	reset: () => void;
	startTracking: (query: string) => void;

	// Helpers
	hasResults: boolean;
	isComplete: boolean;
	getObjectTypeStatus: (
		objectType: ObjectType
	) => 'pending' | 'processing' | 'completed';
}

/**
 * Hook pour gérer le flux de messages de scénario
 */
export function useScenarioMessages(
	options: UseScenarioMessagesOptions
): UseScenarioMessagesReturn {
	const { projectId, enabled = true, callbacks = {} } = options;

	// Ref pour les callbacks (évite les re-renders)
	const callbacksRef = useRef<ScenarioMessageCallbacks>(callbacks);
	callbacksRef.current = callbacks;

	// Reducer avec callbacks
	const reducer = useCallback(createReducer(callbacksRef), []);
	const [state, dispatch] = useReducer(
		reducer,
		undefined,
		createInitialState
	);

	// Subscription AppSync — `onData` (vs `data`) garantit qu'aucun message n'est
	// perdu quand plusieurs publishProjectMessage arrivent en rafale (React batch
	// les rerenders, donc `data` ne contiendrait que le dernier).
	useSubscription(subscribeToMessages, {
		variables: { projectId: projectId || '' },
		skip: !projectId || !enabled,
		onData: ({ data }) => {
			const raw = data?.data?.onProjectMessage?.message;
			if (!raw) return;
			const message = parseScenarioMessage(raw);
			if (message) {
				dispatch({ type: 'MESSAGE', payload: message });
			}
		}
	});

	// Actions
	const reset = useCallback(() => {
		dispatch({ type: 'RESET' });
	}, []);

	const startTracking = useCallback((query: string) => {
		dispatch({ type: 'START', payload: { query } });
	}, []);

	// Helpers
	const hasResults =
		state.results.stakeholders !== null ||
		state.results.stakes !== null ||
		state.results.risks !== null ||
		state.results.indicators !== null;

	const isComplete =
		state.currentStep === 'completed' && state.finalResult !== null;

	const getObjectTypeStatus = useCallback(
		(objectType: ObjectType): 'pending' | 'processing' | 'completed' => {
			// Types spéciaux
			if (objectType === 'news') {
				return state.news !== null ? 'completed' : 'pending';
			}
			if (objectType === 'scenario_info') {
				return state.scenarioInfo.name !== null
					? 'completed'
					: 'pending';
			}

			// Types d'objets
			const objectTypes = [
				'stakeholders',
				'stakes',
				'risks',
				'indicators'
			] as const;
			if (
				!objectTypes.includes(
					objectType as (typeof objectTypes)[number]
				)
			) {
				return 'pending';
			}

			const result =
				state.results[objectType as keyof typeof state.results];
			if (result !== null) {
				return 'completed';
			}

			// Vérifier si on est en train de traiter ce type
			if (state.currentStep === `processing_${objectType}`) {
				return 'processing';
			}

			return 'pending';
		},
		[state]
	);

	return {
		// État
		state,
		isRunning: state.isRunning,
		progress: state.progress,
		currentStep: state.currentStep,
		error: state.error,

		// Résultats partiels
		news: state.news,
		scenarioInfo: state.scenarioInfo,
		results: state.results,
		finalResult: state.finalResult,

		// Actions
		reset,
		startTracking,

		// Helpers
		hasResults,
		isComplete,
		getObjectTypeStatus
	};
}

/**
 * Hook simplifié pour juste écouter les messages sans gestion d'état
 */
export function useScenarioMessageListener(
	projectId: string | null,
	onMessage: (message: ScenarioMessage) => void,
	enabled = true
): void {
	const onMessageRef = useRef(onMessage);
	onMessageRef.current = onMessage;

	useSubscription(subscribeToMessages, {
		variables: { projectId: projectId || '' },
		skip: !projectId || !enabled,
		onData: ({ data }) => {
			const raw = data?.data?.onProjectMessage?.message;
			if (!raw) return;
			const message = parseScenarioMessage(raw);
			if (message) {
				onMessageRef.current(message);
			}
		}
	});
}
